import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { AppContextMenu } from '@/UI/AppContextMenu/AppContextMenu';
import { I18N_KEYS } from '@/language/keys';
import Actions from '@/components/Menu/Actions';
import { useNodeActions } from '@/components/Nodes/useNodeActions';
import { useInteractiveDownloader } from '@/hooks/useInteractiveDownloader';
import { useSaveRecipe } from '@/hooks/useSaveRecipe';
import { useWorkflowStore } from '@/state/workflow.state';
import { useCustomGroupView, useEffectiveIsLocked, useNode, useNodeAssetManager } from '../FlowContext';
import { DEFAULT_GROUP_COLOR, LABEL_SIZE } from '../Views/NodeGrouping/groups.types';
import ParentMenuItem from './ParentMenuItem';
import { ClearAssetsSubmenu } from './ClearAssetsSubmenu';
import { AddNodeMenu } from './AddNodeMenuContent';
import { ColorMenuItem } from './menu-items/ColorMenuItem';
import type { MenuAction } from '@/components/Menu/Actions';
import type { AddNewNodeOptions } from '../flowTypes';

interface GroupContextMenuProps {
  positionX: number;
  positionY: number;
  isOpen: boolean;
  onClose: () => void;
  groupNodeId: string;
  addNewNode: ({ action, dropX, dropY, connection }: AddNewNodeOptions) => void;
}

export const GroupContextMenu = ({
  positionX,
  positionY,
  isOpen,
  onClose,
  groupNodeId,
  addNewNode,
}: GroupContextMenuProps) => {
  const { t } = useTranslation();
  const groupNode = useNode(groupNodeId);
  const customGroupView = useCustomGroupView(groupNodeId);
  const { deleteNodes, duplicateNodes } = useNodeActions();
  const nodeAssetManager = useNodeAssetManager();
  const { id: recipeId } = useWorkflowStore((state) => state.recipe);
  const recipeName = useWorkflowStore((state) => state.recipe.name);
  const saveRecipe = useSaveRecipe();
  const { downloadFromMultipleNodesWithProgress } = useInteractiveDownloader();

  const handleDuplicate = useCallback(() => {
    duplicateNodes([groupNodeId]);
    onClose();
  }, [duplicateNodes, groupNodeId, onClose]);

  const handleUngroup = useCallback(() => {
    customGroupView.ungroup();
    onClose();
  }, [customGroupView, onClose]);

  const handleDelete = useCallback(() => {
    deleteNodes([groupNodeId]);
    onClose();
  }, [deleteNodes, groupNodeId, onClose]);

  const handleResizeToFit = useCallback(() => {
    customGroupView.resizeToFit();
    onClose();
  }, [customGroupView, onClose]);

  const handleColorChange = useCallback(
    (color: string) => {
      // Update the group node's color
      customGroupView.updateGroupColor(color);
    },
    [customGroupView],
  );

  const handleLabelSizeChange = useCallback(
    (labelFontSize: number) => {
      customGroupView.updateGroupLabelSize(labelFontSize);
      onClose();
    },
    [customGroupView, onClose],
  );

  const handleToggleLock = useCallback(() => {
    customGroupView.toggleLock();
    onClose();
  }, [customGroupView, onClose]);

  const handleDownloadAllAssets = useCallback(() => {
    void (async () => {
      const nodesWithAssets = await nodeAssetManager.collectAssetsFromNodes([groupNode], recipeId, saveRecipe);
      await downloadFromMultipleNodesWithProgress(
        nodesWithAssets,
        `${recipeName}${groupNode.data.name ? `_${groupNode.data.name}` : ''}`,
      );
      onClose();
    })();
  }, [groupNode, nodeAssetManager, recipeId, saveRecipe, downloadFromMultipleNodesWithProgress, recipeName, onClose]);

  // Get current group data
  const currentColor = customGroupView.getGroupColor() || DEFAULT_GROUP_COLOR;
  const currentLabelSize = customGroupView.getGroupLabelSize() || LABEL_SIZE.SMALL;
  const isLocked = useEffectiveIsLocked(groupNodeId);
  const children = customGroupView.getChildNodes();
  const hasLockedChildren = children.some((child) => child.data.isLocked);
  const hasDownloadable = useMemo(
    () => nodeAssetManager.hasDownloadableNodes([groupNode]),
    [nodeAssetManager, groupNode],
  );

  const menu: MenuAction[] = useMemo(
    () => [
      {
        type: 'slot' as const,
        el: renderNewNodeMenuItem(t, addNewNode, positionX, positionY, onClose),
      },
      { type: 'divider' },
      {
        name: t(I18N_KEYS.RECIPE_MAIN.GROUP_CONTEXT_MENU.DOWNLOAD_ALL),
        action: handleDownloadAllAssets,
        disabled: !hasDownloadable,
      },
      {
        type: 'slot' as const,
        el: (
          <ClearAssetsSubmenu
            nodes={[groupNode]}
            onClose={onClose}
            clearAllLabel={t(I18N_KEYS.RECIPE_MAIN.GROUP_CONTEXT_MENU.CLEAR_ALL_ASSETS)}
          />
        ),
      },
      { type: 'divider' },
      { type: 'section_label', text: t(I18N_KEYS.RECIPE_MAIN.GROUP_CONTEXT_MENU.GROUP_ACTIONS) },
      { name: t(I18N_KEYS.RECIPE_MAIN.GROUP_CONTEXT_MENU.DUPLICATE), action: handleDuplicate },
      {
        name: isLocked
          ? t(I18N_KEYS.RECIPE_MAIN.GROUP_CONTEXT_MENU.UNLOCK)
          : t(I18N_KEYS.RECIPE_MAIN.GROUP_CONTEXT_MENU.LOCK),
        action: handleToggleLock,
      },
      {
        type: 'slot' as const,
        el: <ColorMenuItem currentColor={currentColor} onColorChange={handleColorChange} />,
      },
      {
        type: 'slot' as const,
        el: renderLabelSizeMenuItem(t, handleLabelSizeChange, currentLabelSize),
      },
      {
        name: t(I18N_KEYS.RECIPE_MAIN.GROUP_CONTEXT_MENU.RESIZE_TO_FIT),
        action: handleResizeToFit,
        disabled: !customGroupView.hasAnyChildren(),
      },
      {
        name: t(I18N_KEYS.RECIPE_MAIN.GROUP_CONTEXT_MENU.UNGROUP),
        action: handleUngroup,
      },
      {
        name: t(I18N_KEYS.RECIPE_MAIN.GROUP_CONTEXT_MENU.DELETE),
        action: handleDelete,
        disabled: isLocked || hasLockedChildren,
      },
    ],
    [
      t,
      addNewNode,
      positionX,
      positionY,
      onClose,
      groupNode,
      handleDownloadAllAssets,
      hasDownloadable,
      isLocked,
      handleToggleLock,
      currentColor,
      handleColorChange,
      currentLabelSize,
      handleLabelSizeChange,
      handleResizeToFit,
      customGroupView,
      handleUngroup,
      handleDelete,
      handleDuplicate,
      hasLockedChildren,
    ],
  );

  return (
    <AppContextMenu open={isOpen} onClose={onClose} mouseX={positionX} mouseY={positionY} items={menu} width="200px" />
  );
};

function renderNewNodeMenuItem(
  t: (key: string) => string,
  addNewNode: ({ action, dropX, dropY, connection }: AddNewNodeOptions) => void,
  positionX: number,
  positionY: number,
  onClose: () => void,
) {
  return (
    <ParentMenuItem
      label={t(I18N_KEYS.RECIPE_MAIN.GROUP_CONTEXT_MENU.NEW_NODE)}
      contextMenuWidth="200px"
      autoFocus={false}
    >
      <AddNodeMenu
        autoFocus={false}
        positionX={positionX}
        positionY={positionY}
        addNewNode={addNewNode}
        onClose={onClose}
      />
    </ParentMenuItem>
  );
}

function renderLabelSizeMenuItem(
  t: (key: string) => string,
  handleLabelSizeChange: (labelFontSize: number) => void,
  currentLabelSize: LABEL_SIZE,
) {
  return (
    <ParentMenuItem
      label={t(I18N_KEYS.RECIPE_MAIN.GROUP_CONTEXT_MENU.LABEL_SIZE)}
      offset={{ left: '10px' }}
      contextMenuWidth="180px"
    >
      <Actions
        items={[
          {
            name: t(I18N_KEYS.RECIPE_MAIN.GROUP_CONTEXT_MENU.LABEL_SIZE_OPTIONS.SMALL),
            action: () => handleLabelSizeChange(LABEL_SIZE.SMALL),
            selected: currentLabelSize === LABEL_SIZE.SMALL,
          },
          {
            name: t(I18N_KEYS.RECIPE_MAIN.GROUP_CONTEXT_MENU.LABEL_SIZE_OPTIONS.MEDIUM),
            action: () => handleLabelSizeChange(LABEL_SIZE.MEDIUM),
            selected: currentLabelSize === LABEL_SIZE.MEDIUM,
          },
          {
            name: t(I18N_KEYS.RECIPE_MAIN.GROUP_CONTEXT_MENU.LABEL_SIZE_OPTIONS.LARGE),
            action: () => handleLabelSizeChange(LABEL_SIZE.LARGE),
            selected: currentLabelSize === LABEL_SIZE.LARGE,
          },
        ]}
        fontSize="0.75rem"
      />
    </ParentMenuItem>
  );
}
